import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { createStructuredSelector } from 'reselect';
import { Plus } from 'react-feather';

import { Search, Loading } from 'carbon-components-react';

import MainHeader from '../../components/main-header/main-header.component';
import Mainmenu from '../../components/menu/main-menu/main-menu.component';
import AddClassModal from '../../components/modal/add-class/add-class.component';
import AddTermModal from '../../components/modal/add-term/add-term.component';
import AddStudentModal from '../../components/modal/add-student/add-student.component';
import QuickActionCard, {
  AddCard,
} from '../../components/card/quick-action-card/quick-action-card.component';
import AddClassLabelModal from '../../components/modal/add-class-label/add-class-label.component';
import ClassCard from '../../components/card/class-card/class-card.component';
import Footer from '../../components/footer/footer.component';
import ConfirmDeleteModal from '../../components/modal/confirm-delete/confirm-delete.component';

// import our redux stuffs
import {
  selectClasses,
  selectClassLabels,
} from '../../redux/selectors/class.selectors';
import { selectCurrentUser } from '../../redux/selectors/user.selectors';
import {
  setClasses,
  removeClass,
  setClassLabels,
} from '../../redux/actions/class.actions';

// import api
import {
  getClasses,
  orderClass,
  deleteClass,
  getClassLabels,
  archiveClass,
} from '../../api/class.service';

//import svg icon
import { ReactComponent as Target } from '../../assets/images/card-icons/target.svg';
import { ReactComponent as Teamwork } from '../../assets/images/card-icons/teamwork.svg';
import { ReactComponent as SortIcon } from '../../assets/images/card-icons/sort.svg';
import { ReactComponent as Graduated } from '../../assets/images/card-icons/graduated.svg';

import './classes.styles.scss';
import EditClass from './EditClass';
import OrderClassesModal from './OrderClassesModal';
import SetGraduatingClassModal from './SetGraduatingClassModal';
import ComponentGuard from '@/components/component-guard/ComponentGuard';
import { CLASS_SUB_ROLES } from '@/utils/user-roles';

const plusIcon = <Plus size="25" strokeWidth="2px" />;

class ClassesPage extends React.Component {
  constructor(props) {
    super(props);

    // set state
    this.state = {
      query: '',
      loading: props.classes.length === 0,
      classes: props.classes,
      errorMessage: null,
      openAddClassModal: false,
      openAddTermModal: false,
      openAddStudentModal: false,
      openAddClassLabelModal: false,
      openEditClassModal: false,
      openConfirmDeleteModal: false,
      openOrderClassesModal: false,
      openSetGraduatingClassModal: false,
      selectedClass: null,
      deleteDescription:
        'Please confirm class deletion. This action cannot be reversed.',
      deleteAction: () => {},
      deleteSucessAction: () => {},
    };
  }

  componentDidMount() {
    // always fetch the class list, the student count on the cards comes from it
    // and must never be served from a stale redux copy
    this.fetchClasses();

    if (this.props.classLabels.length === 0) {
      getClassLabels(this.props.currentUser.authToken).then((response) => {
        // save to redux
        this.props.setClassLabels(response.classLabels);
      });
    }
  }

  fetchClasses = async () => {
    // show loading only when there is nothing to display yet
    if (!this.state.loading && this.state.classes.length === 0)
      this.setState({ loading: true, errorMessage: null });

    // initiate request
    const response = await getClasses(this.props.currentUser.authToken);

    // request failed, show error message
    if (!response.success) {
      this.setState({ loading: false, errorMessage: response.message });

      return;
    }

    // successful, the server response is the source of truth for the cards
    this.setState({
      loading: false,
      errorMessage: null,
      classes: response.classes,
    });

    // save to redux for the other pages that read the class list from there
    this.props.setClasses(response.classes);
  };

  render() {
    const classes =
      this.state.query.length > 0
        ? this.state.classes.filter(
            (item) =>
              item.description
                .toLowerCase()
                .includes(this.state.query.toLowerCase()) ||
              item.uniqueId
                .toLowerCase()
                .includes(this.state.query.toLowerCase()),
          )
        : this.state.classes;

    return (
      <div>
        <MainHeader />

        <AddClassModal
          openModal={this.state.openAddClassModal}
          class={this.state.selectedClass}
          cancelAction={() => this.setState({ openAddClassModal: false })}
          onClassSaved={() => this.fetchClasses()}
        />
        <EditClass
          isOpen={this.state.openEditClassModal}
          classItem={this.state.selectedClass}
          closeModal={() => this.setState({ openEditClassModal: false })}
          refetchClasses={this.fetchClasses}
        />

        <AddTermModal
          openModal={this.state.openAddTermModal}
          cancelAction={() => this.setState({ openAddTermModal: false })}
        />

        <AddStudentModal
          openModal={this.state.openAddStudentModal}
          closeAction={() => this.setState({ openAddStudentModal: false })}
        />

        <AddClassLabelModal
          openModal={this.state.openAddClassLabelModal}
          cancelAction={() => this.setState({ openAddClassLabelModal: false })}
        />

        <ConfirmDeleteModal
          openModal={this.state.openConfirmDeleteModal}
          description={this.state.deleteDescription}
          deleteAction={this.state.deleteAction}
          deleteSuccessAction={this.state.deleteSucessAction}
          cancelAction={() => this.setState({ openConfirmDeleteModal: false })}
        />

        <OrderClassesModal
          openModal={this.state.openOrderClassesModal}
          classes={this.state.classes}
          cancelAction={() => this.setState({ openOrderClassesModal: false })}
          refreshClasses={this.fetchClasses}
        />

        <SetGraduatingClassModal
          isOpen={this.state.openSetGraduatingClassModal}
          closeModal={() =>
            this.setState({ openSetGraduatingClassModal: false })
          }
          refreshClasses={this.fetchClasses}
        />

        <div className="classes page-container">
          <div>
            <span className="h-18">Quick Actions</span>
            <span className="right">
              {' '}
              <Mainmenu />{' '}
            </span>
          </div>

          <div className="card-row-5">
            <AddCard
              title="ADD A NEW CLASS"
              onClick={() => {
                this.setState({
                  openAddClassModal: true,
                  selectedClass: null,
                });
              }}
            />

            <QuickActionCard
              description="Class Labels"
              leftActionText="ADD"
              rightActionText="VIEW"
              actionEvent1={() => {
                this.setState({ openAddClassLabelModal: true });
              }}
              actionEvent2={() => {
                this.props.history.push('/class-label');
              }}
            >
              <Target color="#fff" />
            </QuickActionCard>

            <QuickActionCard
              description="Students"
              leftActionText="ADD"
              rightActionText="VIEW"
              actionEvent1={() => {
                this.setState({ openAddStudentModal: true });
              }}
              actionEvent2={() => this.props.history.push('/student')}
            >
              <Teamwork />
            </QuickActionCard>

            <ComponentGuard
              allowedRoles={[CLASS_SUB_ROLES.CLASS_MANAGEMENT_ACCESS]}
            >
              <QuickActionCard
                description="Order Classes"
                leftActionText="ORDER"
                hideAction2
                actionEvent1={() => {
                  this.setState({ openOrderClassesModal: true });
                }}
              >
                <SortIcon />
              </QuickActionCard>

              <QuickActionCard
                description="Set Graduating Class"
                leftActionText="SET"
                hideAction2
                actionEvent1={() => {
                  this.setState({ openSetGraduatingClassModal: true });
                }}
              >
                <Graduated />
              </QuickActionCard>
            </ComponentGuard>
          </div>

          <hr className="grey-hr mt-30" />

          <div className="mt-20">
            <span className="h-24">Classes</span>&nbsp;
          </div>

          <div className="mt-10">
            <Search
              id="query"
              name="query"
              value={this.state.query}
              placeHolderText="Search by name of class"
              onChange={(event) => this.setState({ query: event.target.value })}
            />
          </div>

          <div className="mt-20" hidden={!this.state.loading}>
            <Loading
              small={true}
              withOverlay={false}
              description="Loading..."
            />
          </div>

          <div className="mt-30" hidden={this.state.errorMessage == null}>
            <span className="mt-10 text-danger">
              {this.state.errorMessage}.
            </span>
            &nbsp;
            <span
              className="text-primary pointer font-bold"
              onClick={() => this.fetchClasses()}
            >
              TRY AGAIN
            </span>
          </div>

          <p
            className="text-14 mt-30 text-grey"
            hidden={
              this.state.classes.length > 0 ||
              this.state.loading ||
              this.state.errorMessage !== null
            }
          >
            No class added
          </p>

          <div className="bx--row mb-40 mt-30">
            {classes
              // .sort((a, b) => a.classPosition - b.classPosition)
              .map((item) => (
                <div className="bx--col-md-2 fade-in">
                  <ClassCard
                    class={item}
                    clickAction={() =>
                      this.props.history.push(`/class/${item.id}`)
                    }
                    editAction={() =>
                      this.setState({
                        openEditClassModal: true,
                        selectedClass: item,
                      })
                    }
                    deleteAction={() =>
                      this.setState({
                        openConfirmDeleteModal: true,
                        deleteAction: async () => {
                          return await deleteClass(
                            this.props.currentUser.authToken,
                            {
                              id: item.id,
                              description: item.description,
                            },
                          );
                        },
                        deleteSucessAction: () => {
                          this.props.removeClass(item.id);
                          this.fetchClasses();
                        },
                        deleteDescription: `Confirm deletion of ${item.description}. This action cannot be reversed`,
                      })
                    }
                    archiveAction={() =>
                      this.setState({
                        openConfirmDeleteModal: true,
                        deleteAction: async () => {
                          return await archiveClass(
                            this.props.currentUser.authToken,
                            {
                              ...item,
                              archived: true,
                            },
                          );
                        },
                        deleteSucessAction: () => {
                          this.props.removeClass(item.id);
                          this.fetchClasses();
                        },
                        deleteDescription: `Do you want to archive this class ${item.description}. This action cannot be reversed`,
                      })
                    }
                  />
                </div>
              ))}
          </div>

          <Footer fixed={false} showLinks={false} />
        </div>
      </div>
    );
  }
}

// this will provide us with the state values we need
const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
  classes: selectClasses,
  classLabels: selectClassLabels,
});

// this allows us to map our redux actions to our component
const mapDispatchToProps = (dispatch) => ({
  setClasses: (classes) => dispatch(setClasses(classes)),
  setClassLabels: (classLabels) => dispatch(setClassLabels(classLabels)),
  removeClass: (classId) => dispatch(removeClass(classId)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(ClassesPage),
);
